import { Button, Flex, FormItemsGroup, NavItem, Select, Space, Spacing, Text, Tooltip } from '@billy/design-system'
import { addDays } from 'date-fns'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useZervantSettings } from '@features/invoicingSettings/hooks/useZervantSettings'
import { getDisplayDueDateLabel } from '@views/externalInvoices/routes/InvoiceEditor/elements/InvoiceEditorContent/elements/InvoiceEditorForm/elements/Payments/elements/InvoicePaymentTermAndDueDate/utils/getDisplayDueDateLabel'
import { getItems } from '@views/externalInvoices/routes/InvoiceEditor/elements/InvoiceEditorContent/elements/InvoiceEditorForm/elements/Payments/elements/InvoicePaymentTermAndDueDate/utils/getItems'

import { CoinsDollarEuro } from '../../../../assets/images'
import { useFormContext, useWatch } from '../../../../hooks'
import { useDemoInvoiceLocale } from '../../hooks/useDemoInvoiceLocale'
import { DemoInvoiceFormSchema } from '../../types/demoInvoiceFormSchema'
import * as Styled from './styles'

export const DemoInvoicePayments = (): ReactElement => {
  const { t } = useTranslation()
  const locale = useDemoInvoiceLocale()
  const { data: zervantSettings } = useZervantSettings()
  const { control, FormItem, setValue } = useFormContext<DemoInvoiceFormSchema>()
  const issueDate = useWatch({ control, name: 'issueDate' })
  const dueDate = useWatch({ control, name: 'dueDate' })

  const paymentTermItems: NavItem<number>[] = useMemo(
    () => getItems(zervantSettings?.managedFields?.PAYMENT_TERMS ?? [], t, issueDate),
    [issueDate, t, zervantSettings?.managedFields?.PAYMENT_TERMS],
  )

  const handlePaymentTermSelect = useCallback(
    (onChange: (paymentTerm: number) => void, paymentTerm?: number) => {
      if (paymentTerm === undefined) {
        return
      }

      onChange(paymentTerm)
      setValue('dueDate', addDays(issueDate, paymentTerm))
    },
    [issueDate, setValue],
  )

  return (
    <Flex flexDirection="column">
      <FormItemsGroup>
        <FormItem
          name="paymentTerm"
          label={
            <Flex flexDirection="column">
              <Text variant="h4">{t('external_invoices.editor.form.field.due_date_and_payment_term.header')}</Text>
              <Space />
              <Text colorVariant="secondary">
                {t('external_invoices.editor.form.field.due_date_and_payment_term.label')}
              </Text>
            </Flex>
          }
          render={({ field: { onChange, value, ...rest } }) => (
            <Select<number>
              {...rest}
              dropdownSize="fitTrigger"
              getDisplayValue={() => ''}
              items={paymentTermItems}
              onSelect={(_id, paymentTerm) => handlePaymentTermSelect(onChange, paymentTerm)}
              placeholder=""
              placement="top"
              prefix={getDisplayDueDateLabel({ dueDate, issueDate, locale, numberOfDaysSelected: value, t })}
              selectedId={String(value)}
              selectLook={false}
              size="sm"
              withSearch={false}
            />
          )}
        />
      </FormItemsGroup>
      <Space size="s24" />
      <Styled.PaymentMethodsBannerWrapper>
        <Flex alignItems="center" gap={Spacing.S16} justifyContent="space-between">
          <Flex alignItems="center" gap={Spacing.S16}>
            <CoinsDollarEuro />
            <Text>{t('external_invoices.editor.payment_methods.banner.how_to_receive_payments')}</Text>
          </Flex>
          <Tooltip label={t('demo_invoice.unavailable_action')} placement="top">
            <Button color="primary" disabled icon="handCoins" size="sm" variant="onColor">
              {t('external_invoices.editor.payment_methods.banner.setup_payment')}
            </Button>
          </Tooltip>
        </Flex>
      </Styled.PaymentMethodsBannerWrapper>
    </Flex>
  )
}
